/**
 * RhfComputed — display-only adapter that renders a disabled text input
 * showing a computed value derived from other form fields.
 *
 * Usage in schema:
 * { name: 'totalValue', def: { adapterHint: 'computed', label: 'Total', schema: z.any() },
 *   adapterProps: { compute: { operator: 'custom', id: 'calcTotal' } } }
 */

import * as React from 'react';
import { useWatch, type Control, type FieldValues } from 'react-hook-form';
import { TextInput } from '../primitives';
import { FormField } from '../patterns';

interface RhfComputedProps {
  name: string;
  control: Control<FieldValues>;
  label?: string;
  placeholder?: string;
  semanticId?: string;
  disabled?: boolean;
  required?: boolean;
  formatter?: (value: string) => string;
  [key: string]: unknown;
}

export const RhfComputed = React.memo(function RhfComputed({
  name,
  control,
  label,
  placeholder,
  semanticId,
  formatter,
}: RhfComputedProps): React.ReactElement {
  const value = useWatch({ control, name });

  const displayValue = React.useMemo(() => {
    if (value == null || value === '') return '';
    const strVal = String(value);
    return formatter ? formatter(strVal) : strVal;
  }, [value, formatter]);

  return (
    <FormField label={label} semanticId={semanticId}>
      <TextInput
        value={displayValue}
        placeholder={placeholder}
        disabled
        readOnly
        onChange={() => {}}
      />
    </FormField>
  );
});
